/**
 * TSV translated-text fitter
 * ---------------------------------------------------------------------------
 * Shrinks a label to fit its box instead of letting the box grow. Written for
 * German and Swedish, where a translated button can be 40-90% longer than the
 * English it replaced.
 *
 * Pairs with assets/tsv-i18n-fit.css, which gives the same elements permission
 * to shrink (min-width: 0). Without that, a flex child never drops below its
 * content width and nothing ever reports as overflowing.
 *
 * Does nothing on the English storefront.
 *
 * How it decides an element needs shrinking:
 *   1. its own content is clipped   (scrollWidth  > clientWidth)
 *   2. its text wrapped taller than its box  (scrollHeight > clientHeight)
 *   3. it is pushing its parent wider than the parent's content box, and the
 *      parent is not an intentional horizontal scroller (carousel, chip rail)
 *
 * Case 3 is the common one on this theme: a button with `width: auto` inside a
 * non-flex parent simply grows to fit its text and reports no overflow of its
 * own -- the damage shows up one level up, as the section widening.
 *
 * Font-size steps down 4% at a time to a floor of 78%. If it still does not fit
 * at the floor we stop shrinking and let it wrap (data-tsv-fitted="wrapped") --
 * unreadable text is worse than two lines.
 */
(function () {
  'use strict';

  var lang = (document.documentElement.getAttribute('lang') || 'en').toLowerCase();
  if (lang.indexOf('en') === 0) return;           // English is untouched

  var FLOOR = 0.78;      // smallest we will go
  var STEP  = 0.04;      // 4% per pass
  var MAXLEN = 120;      // longer than this is prose: let it wrap, do not shrink
  var SEL = [
    '.tsv-fit',
    'button', '.btn', 'summary',
    '[class*="__btn"]', '[class*="-btn"]',
    '[class*="__cta"]',
    '[class*="chip" i]', '[class*="pill" i]', '[class*="badge" i]',
    '[class*="__tab"]', '[class*="label" i]',
    // tracked uppercase micro-copy and single-line values found across the theme
    '[class*="eyebrow" i]', '[class*="__heading"]', '[class*="__title"]',
    '[class*="__link"]', '[class*="nav-item"]', '[class*="__trigger"]',
    '[class*="__meta"]', '[class*="__count"]', '[class*="__code"]',
    '[class*="__price"]', '[class*="__total"]', '[class*="__value"]',
    '[class*="__status"]', '[class*="__state"]', '[class*="tier" i]',
    '[class*="__hint"]', '[class*="__note"]', '[class*="__sub"]',
    '[class*="__name"]', '[class*="__date"]', '[class*="__amount"]',
    '[class*="__delta"]', '[class*="__brand"]', '[class*="__countdown"]',
    '[class*="__refresh"]', '[class*="__vendor"]', '[class*="__greeting"]'
  ].join(',');

  var queued = new Set();
  var frame = null;

  function isScroller(el) {
    var ox;
    try { ox = window.getComputedStyle(el).overflowX; } catch (_) { return false; }
    return ox === 'auto' || ox === 'scroll';
  }

  // width of the parent's content box, i.e. what a child actually has to fit in
  function contentWidth(el) {
    var cs;
    try { cs = window.getComputedStyle(el); } catch (_) { return el.clientWidth; }
    var pl = parseFloat(cs.paddingLeft) || 0;
    var pr = parseFloat(cs.paddingRight) || 0;
    return el.clientWidth - pl - pr;
  }

  // Heavy uppercase tracking costs far more width on OFFNUNGSZEITEN than on
  // HOURS. Where a label is tracked hard AND does not fit, ease the tracking
  // before touching the font size -- it is the cheaper, less visible fix.
  // Only ever reduces, and only on elements that actually overflow.
  function trimTracking(el) {
    var cs;
    try { cs = window.getComputedStyle(el); } catch (_) { return false; }
    var ls = parseFloat(cs.letterSpacing);
    var fs = parseFloat(cs.fontSize) || 16;
    if (!isFinite(ls) || ls <= 0) return false;
    if (ls / fs <= 0.08) return false;                 // already tight enough
    el.style.letterSpacing = '0.06em';                 // em: follows the scaled size
    return true;
  }

  function overflows(el) {
    // 1. own content is clipped (1px tolerance for sub-pixel rounding)
    if (el.scrollWidth > el.clientWidth + 1) return true;
    // 2. text wrapped into more height than the box allows
    if (el.scrollHeight > el.clientHeight + 1) return true;
    // 3. this element is what is stretching its parent
    var p = el.parentElement;
    if (p && p.clientWidth > 0 && !isScroller(p)) {
      if (p.scrollWidth > p.clientWidth + 1) {
        var avail = contentWidth(p);
        if (avail > 0 && el.getBoundingClientRect().width > avail + 1) return true;
      }
    }
    // 4. it fits its own box, but it has wrapped out the TOP or BOTTOM of an
    //    ancestor that was given a fixed height for the English string. The
    //    minis banner is exactly this: a 66px band whose heading wraps to three
    //    lines in French and spills over the bottle photos. Case 2 cannot see it
    //    because the heading's own box grows freely; the band is what is fixed.
    //    Images are ignored on purpose -- the bottles break out of that band by
    //    design and must not be touched.
    var r = el.getBoundingClientRect();
    var a = el.parentElement;
    for (var d = 0; a && d < 3; d++, a = a.parentElement) {
      var acs;
      try { acs = window.getComputedStyle(a); } catch (_) { break; }
      if (acs.height === 'auto') continue;                       // free to grow: not a constraint
      if (acs.overflowY === 'auto' || acs.overflowY === 'scroll') break;
      var ar = a.getBoundingClientRect();
      if (ar.height < 8) continue;
      var pt = parseFloat(acs.paddingTop) || 0;
      var pb = parseFloat(acs.paddingBottom) || 0;
      if (r.bottom > ar.bottom - pb + 1 || r.top < ar.top + pt - 1) return true;
    }
    return false;
  }

  function fit(el) {
    if (!el || !el.isConnected) return;
    // skip anything with no text of its own, or an image/icon-only control
    var text = (el.textContent || '').trim();
    if (!text || text.length < 2) return;
    if (text.length > MAXLEN) return;               // prose: wrapping is correct
    if (el.getAttribute('data-tsv-fit-skip') !== null) return;
    // if a smaller label inside also qualifies, fit that instead of the wrapper
    try { if (el.querySelector(SEL)) return; } catch (_) {}

    // reset before measuring so a resize can give width back
    el.style.removeProperty('--tsv-fit');
    el.style.removeProperty('letter-spacing');
    el.style.removeProperty('height');
    el.style.removeProperty('min-height');
    el.removeAttribute('data-tsv-fitted');
    if (!overflows(el)) return;

    el.setAttribute('data-tsv-fitted', '');
    if (trimTracking(el) && !overflows(el)) return;    // tracking alone was enough

    var scale = 1;
    while (scale > FLOOR) {
      scale = Math.round((scale - STEP) * 100) / 100;
      el.style.setProperty('--tsv-fit', scale);
      if (!overflows(el)) return;
    }
    // still too long at the floor: let it wrap rather than shrink further
    el.setAttribute('data-tsv-fitted', 'wrapped');

    // A box with a hard height cannot show a second line -- wrapping there just
    // clips it. Release the height as a last resort and keep the original as a
    // floor: a badge six pixels taller reads better than a sentence cut in half.
    if (el.scrollHeight > el.clientHeight + 1) {
      el.style.minHeight = Math.round(el.getBoundingClientRect().height) + 'px';
      el.style.height = 'auto';
      el.setAttribute('data-tsv-fitted', 'relaxed');
    }
  }

  function flush() {
    frame = null;
    var batch = Array.from(queued);
    queued.clear();
    // read/write in one pass; the browser batches the reflow
    batch.forEach(fit);
  }

  function schedule(el) {
    queued.add(el);
    if (frame) return;
    frame = requestAnimationFrame(flush);
  }

  function scan(root) {
    var scope = root && root.querySelectorAll ? root : document;
    var nodes;
    try { nodes = scope.querySelectorAll(SEL); } catch (_) { return; }
    for (var i = 0; i < nodes.length; i++) schedule(nodes[i]);
    if (root && root.matches && root.matches(SEL)) schedule(root);
  }

  function boot() {
    scan(document);

    // dynamically rendered UI: cards, chips, the concierge, account panels
    if (window.MutationObserver) {
      var mo = new MutationObserver(function (records) {
        for (var i = 0; i < records.length; i++) {
          var rec = records[i];
          var added = rec.addedNodes;
          for (var j = 0; j < added.length; j++) {
            if (added[j].nodeType === 1) scan(added[j]);
          }
          // a label whose text was swapped in place (innerHTML/textContent):
          // the added nodes are text nodes, so re-measure the target itself
          var t = rec.target;
          // characterData records target the text node itself
          if (t && t.nodeType === 3) t = t.parentElement;
          if (t && t.nodeType === 1 && t.matches) {
            if (t.matches(SEL)) schedule(t);
            else if (t.closest) {
              var owner = t.closest(SEL);
              if (owner) schedule(owner);
            }
          }
        }
      });
      mo.observe(document.documentElement, {
        childList: true,
        subtree: true,
        characterData: true
      });
    }

    // width changes: re-measure from scratch (fit() resets before measuring)
    var t = null;
    window.addEventListener('resize', function () {
      if (t) clearTimeout(t);
      t = setTimeout(function () { scan(document); }, 200);
    }, { passive: true });

    // webfonts land after first paint and change every measurement
    if (document.fonts && document.fonts.ready) {
      document.fonts.ready.then(function () { scan(document); });
    }
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', boot);
  } else {
    boot();
  }
})();
